import { Dialog } from "@/components/ui/design-system";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { deleteCompanyDocument, type CompanyDocument } from "@/lib/companyDocuments";
import { notifyObligationsChanged } from "@/lib/companyObligations";

export function CompanyDocumentDelete({ document, onDeleted }: {
  document: Pick<CompanyDocument, "id" | "naziv">;
  onDeleted: (document: CompanyDocument) => void;
}) {
  const { can, user } = useAuth();
  const canRead = user?.permissions.some((permission) => permission.module === "company_document" && permission.action === "read" && permission.scope === "all") ?? false;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const descriptionId = useId();
  if (!canRead || !can("dokument", "confirm_delete")) return null;
  const remove = async () => {
    setBusy(true); setError(null);
    try {
      const deleted = await deleteCompanyDocument(document.id);
      setOpen(false);
      notifyObligationsChanged();
      onDeleted(deleted);
    } catch (cause: unknown) {
      setError(errorMessage(cause, "Dokument se ne može obrisati. Pokušajte ponovno."));
    } finally { setBusy(false); }
  };
  return <div>
    <Button type="button" variant="danger-subtle" size="sm" aria-label={`Obriši ${document.naziv}`} onClick={() => { setError(null); setOpen(true); }}>Obriši</Button>
    {open && <Dialog descriptionId={descriptionId} title="Obriši dokument iz arhive" onClose={() => { if (!busy) setOpen(false); }}>
      <p id={descriptionId}>Obrisati dokument „{document.naziv}”? Nestat će iz aktivne arhive i više se neće računati kao dokaz obveze. Podaci o brisanju ostaju zabilježeni.</p>
      {error && <p role="alert" className="mt-2 text-[var(--color-destructive)]">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" variant="danger" disabled={busy} onClick={() => void remove()}>{busy ? "Brisanje…" : "Potvrdi brisanje"}</Button>
        <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen(false)}>Odustani</Button>
      </div>
    </Dialog>}
  </div>;
}
